import React from 'react';
import { Element, Node } from 'domhandler';
import Link from './Link';

interface Props {
    element: Element;
}

const LinkElementConverter = ({ element }: Props) => {
    const url = element.attribs?.href;
    const textExtractedFromElement = getNodeText(element);
    const textTrimmed =
        typeof textExtractedFromElement === 'string'
            ? textExtractedFromElement.trim()
            : '';
    const substringAfterFirstColon = url?.substring(url.indexOf(':') + 1);
    if (substringAfterFirstColon === textTrimmed) {
        // If the text after the first colon is exactly the same as the url, render the link with the text as-is
        return <Link url={url} text={textTrimmed} />;
    }

    // For other links, prepend the text if it's not redundant, so that the actual link is spelled out
    return <LinkWithTextPrepended url={url} text={textTrimmed} />;
};

export default React.memo(LinkElementConverter);

interface PropsLinkWithText {
    text?: string;
    url: string;
}

const LinkWithTextPrepended = React.memo(({ text, url }: PropsLinkWithText) => {
    const textIsRedundant = url?.includes(text);
    return (
        <span>
            {!textIsRedundant && (
                <>
                    {text}
                    {': '}
                </>
            )}
            <Link url={url} />
        </span>
    );
});

LinkWithTextPrepended.displayName = 'LinkWithTextPrepended';

/**
 * Recursively extracts all text content from a domhandler Node (Element, Text, etc).
 * @param node - A domhandler Node (Element, Text, etc)
 * @returns The concatenated text content of the node and its children.
 */
export const getNodeText = (node: Node): string => {
    if (node.type === 'text' && 'data' in node) {
        return node.data as string;
    }
    if ('children' in node && Array.isArray(node.children)) {
        return node.children.map(getNodeText).join('');
    }
    return '';
};
